import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouter } from 'next/router'

import { trackRemoveFromCart } from 'app/components/shared/Analytics/segment'
import { OrderItemInfo, UpdateOrderItemInOrder } from 'appTypes/orders'
import { ItemCard } from 'components/ui/ItemCard'
import apiClient from 'helpers/clients/apiClient'
import { selectOrderState } from 'store/partials/cart/cartSelectors'
import { deleteProductFromOrder, getCart } from 'store/partials/cart/cartSlice'
import { groupAccessoryOrderItems } from 'utils/accessories'
import { STORAGE_KEYS } from 'utils/constants'
import { normalizeUrl } from 'utils/normalizeUrl'
import { getFromStorage } from 'utils/storage'

type Props = {
  orderItemInfo: OrderItemInfo
  isCheckout?: boolean
}

export const CartItem: React.FC<Props> = ({ orderItemInfo, isCheckout = false }) => {
  const router = useRouter()
  const dispatch = useDispatch()
  const { cart } = useSelector(selectOrderState)

  const { orderItem, location } = orderItemInfo
  const { id, rentedFrom, rentedTo, resignFromCleaning, resignFromInsurance } = orderItem

  const isBundle = (orderItem.productData?.products ?? []).length > 1
  const url = `/${isBundle ? 'bundles' : 'products'}/${normalizeUrl(
    orderItem.productData?.name ?? '',
    orderItem.productId,
  )}`

  const handleAdditionalServicesChange = (service: 'cleaning' | 'insurance') => async (e: { checked: boolean }) => {
    const { checked } = e

    let data: UpdateOrderItemInOrder = {}

    if (service === 'cleaning') {
      data = {
        resignFromCleaning: !checked,
      }
    } else {
      data = {
        resignFromInsurance: !checked,
      }
    }

    const accessories = cart?.orderItems.filter(
      (s) =>
        s.orderItem.associatedParentOrderItemId === orderItemInfo.orderItem.id && s.orderItem.productId !== undefined,
    )

    if (accessories) {
      const requestAccessories = groupAccessoryOrderItems(accessories.map((s) => s.orderItem))
      data.accessories = requestAccessories
    }

    await apiClient.updateOrderItemLight(id, data)

    const orderId = getFromStorage(STORAGE_KEYS.orderId)

    if (orderId) {
      const intOrderId = Number(orderId)
      const shouldDisplayLoading = false

      dispatch(getCart(intOrderId, shouldDisplayLoading))
    }
  }

  const deleteItemFromCart = useCallback(() => {
    dispatch(deleteProductFromOrder(id))
    trackRemoveFromCart(orderItem, router.pathname)
  }, [dispatch, orderItem, id, router.pathname])

  return (
    <ItemCard
      orderItemInfo={orderItemInfo}
      marginBottom="20px"
      onDelete={!isCheckout ? deleteItemFromCart : undefined}
      onToggleInsurance={!isCheckout ? handleAdditionalServicesChange('insurance') : undefined}
      onToggleCleaning={!isCheckout ? handleAdditionalServicesChange('cleaning') : undefined}
      hideInsuranceAndCleaningPriceIfResigned={isCheckout}
      editHref={
        !isCheckout
          ? `${
              isBundle ? '/bundles' : '/products'
            }/[name]?isEdit=true&rentedFrom=${rentedFrom?.toISOString()}&rentedTo=${rentedTo?.toISOString()}&orderItemId=${id}&locationId=${
              location?.id ?? 0
            }&regionId=${orderItem.regionId}&isCleaning=${!resignFromCleaning}&isInsurance=${!resignFromInsurance}`
          : undefined
      }
      editAs={
        !isCheckout
          ? `${url}?isEdit=true&rentedFrom=${rentedFrom?.toISOString()}&rentedTo=${rentedTo?.toISOString()}&orderItemId=${id}&locationId=${
              location?.id ?? 0
            }&regionId=${orderItem.regionId}&isCleaning=${!resignFromCleaning}&isInsurance=${!resignFromInsurance}`
          : undefined
      }
    />
  )
}
